/**
 * #466: Registry-getriebener Detail-View-Dispatch (docType → View).
 *
 * Ersetzt verstreute per-doc_type-Checks durch EINE Registry. Die Kind-Basis
 * (timesheet/expense) bleibt der Eingang (globale Produkt-Basis, wie Karten auf
 * /timesheets vs /expenses landen); die Registry wählt INNERHALB die
 * spezialisierte View per `doc_type` der Karte. Reclassify (doc_type-/kind-
 * Wechsel) wählt die View damit deterministisch neu — die Vererbung bricht nicht.
 *
 * Kein data-shape-sniffing: der doc_type kommt aus `manual_doc_type` (überlebt
 * den fail-loud-Pfad) ODER `meta.doc_type` (überlebt den Worker-Erfolgs-Merge).
 * Neue Doc-Typen bekommen ihre View durch EINEN Registry-Eintrag, nicht durch
 * eine neue if-Verzweigung in der Detail-Page.
 */

/** doc_type einer Karte — `manual_doc_type` (fail-loud-Pfad) ODER `meta.doc_type`
 *  (Erfolgs-Merge). undefined bei Legacy-Karten ohne expliziten doc_type. */
export function cardDocType(data: unknown): string | undefined {
  const d = data as { manual_doc_type?: unknown; meta?: { doc_type?: unknown } } | null | undefined;
  const dt = d?.manual_doc_type ?? d?.meta?.doc_type;
  return typeof dt === "string" && dt.length > 0 ? dt : undefined;
}

/** Spezialisierte View innerhalb der Timesheet-Kind-Basis. */
export type TimesheetView = "zulagenblatt" | "standard";
/** Spezialisierte View innerhalb der Expense-Kind-Basis.
 *  `fahrtenbuch` (#469) rendert IN-FRAME (REV-19 Muster 1) die km-Zeilen-View
 *  statt des generischen Diäten-Formulars — erbt Edit/Save/Gate vom Rahmen. */
export type ExpenseView = "excel_import" | "fahrtenbuch" | "standard";

/** Registry: doc_type → Timesheet-View. Nicht gelistete/fehlende doc_types →
 *  "standard" (das Stunden-Grid). */
const TIMESHEET_VIEW_BY_DOCTYPE: Readonly<Record<string, TimesheetView>> = {
  zulagenblatt: "zulagenblatt",
  // Karte 721: Das Blatt «Geleistete Stunden für Zusätzliche Projekte» fährt
  // dasselbe Summenzeilen-Modell und wird von demselben Leser gelesen — es
  // braucht deshalb dieselbe View (Scan links, Summen rechts, editierbar).
  // Genau EIN Registry-Eintrag, wie es der Kopf dieser Datei verspricht.
  projektstunden: "zulagenblatt",
  stundenliste: "standard",
};

/** Registry: doc_type → Expense-View. Nicht gelistete/fehlende doc_types →
 *  "standard" (das Diäten-/Fahrten-Formular). `excel_import` ist ein KIND
 *  (Ingestion-Format, orthogonal zum doc_type) und wird in expenseViewFor
 *  separat behandelt. */
const EXPENSE_VIEW_BY_DOCTYPE: Readonly<Record<string, ExpenseView>> = {
  diaeten: "standard",
  fahrtenbuch: "fahrtenbuch",
};

/** Wählt die spezialisierte Timesheet-View aus dem doc_type der Karte.
 *  Legacy/unbekannt → "standard" (Grid). */
export function timesheetViewFor(data: unknown): TimesheetView {
  const dt = cardDocType(data);
  return dt ? (TIMESHEET_VIEW_BY_DOCTYPE[dt] ?? "standard") : "standard";
}

/** Ist diese Karte ein Fahrtenbuch (Expense-doc_type mit km-Zeilen-View, #469)?
 *  Spiegelt `isZulagenblattData`: true genau dann, wenn die doc_type-Identität
 *  der Karte "fahrtenbuch" ist. Kanban rendert dann ein eigenes Badge (card
 *  #523), damit Fahrtenbücher im Board von generischen Diäten unterscheidbar
 *  sind — analog zum Zulagenblatt-Badge. */
export function isFahrtenbuchData(data: unknown): boolean {
  return cardDocType(data) === "fahrtenbuch";
}

/** Wählt die spezialisierte Expense-View. Das Ingestion-KIND `excel_import`
 *  (Excel statt PDF-Scan) gewinnt vor dem doc_type; sonst entscheidet der
 *  doc_type; Legacy/unbekannt → "standard" (Formular). */
export function expenseViewFor(card: { kind?: string | null; data?: unknown }): ExpenseView {
  if (card.kind === "excel_import") return "excel_import";
  const dt = cardDocType(card.data);
  return dt ? (EXPENSE_VIEW_BY_DOCTYPE[dt] ?? "standard") : "standard";
}
